import { useState } from "react";
import { apiPost } from "./api";
import AccountCard from "./AccountCard";
import DriverCard, { LeaveRoleButtons } from "./DriverCard";
import { useDialog } from "./AppDialog";
import { cityStatusLabel, cityStatusShort } from "./statusLabels";
import { withRussianPrefix } from "./phone";
import TaxiIcon from "./TaxiIcon";
import { Chip, Disclosure, Kv } from "./Disclosure";

const ROLE_LABELS = {
  passenger: "Пассажир",
  driver: "Водитель",
  dispatcher: "Диспетчер",
  region_admin: "Админ региона",
};
const ROLE_ICONS = {
  passenger: "passenger",
  driver: "driver",
  dispatcher: "support",
  region_admin: "map",
};

const STATUS_LABELS = {
  WAITLIST: "в очереди",
  PENDING: "на проверке",
  ACTIVE: "работает",
  REJECTED: "отклонено",
  SUSPENDED: "приостановлено",
};
const STATUS_TONES = {
  WAITLIST: "warn",
  PENDING: "warn",
  ACTIVE: "success",
  REJECTED: "danger",
  SUSPENDED: "danger",
};

// "Кабинет": the taxi profile — who the user is in the city, how to become a
// driver or a dispatcher (concept 1.5). One summary card plus a single-open
// accordion: everything fits the screen, details stay one tap away.
const RolesSection = ({ me, cityId, cities, refreshMe, onLogout, onStartTour }) => {
  const { confirm } = useDialog();
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // null until the user picks a section: before that the section that needs
  // attention opens itself
  const [picked, setPicked] = useState(null);

  const [driverForm, setDriverForm] = useState({
    displayName: "",
    phone: "",
    color: "",
    plate: "",
    brand: "",
  });
  const [dispatcherNote, setDispatcherNote] = useState("");

  const city = cities.find((item) => String(item._id) === String(cityId));
  const memberships = (me?.memberships ?? []).filter(
    (membership) => String(membership.cityId?._id) === String(cityId)
  );
  const driverMembership = memberships.find((membership) => membership.role === "driver");
  const driverProfile = me?.driverProfiles?.find(
    (profile) => String(profile.membershipId) === String(driverMembership?._id)
  );
  const hasDispatcher = memberships.some((membership) => membership.role === "dispatcher");
  const hasPassenger = memberships.some((membership) => membership.role === "passenger");
  // Replayable trainings: only roles the user actually holds in this city
  const trainableRoles = [
    ["passenger", "пассажира"],
    ["driver", "водителя"],
  ].filter(([role]) =>
    memberships.some((membership) => membership.role === role && membership.status === "ACTIVE")
  );
  const trained = (role) => Number(me?.user?.tutorials?.[role] ?? 0) > 0;

  const attention = !me?.user?.phone ? "profile" : memberships.length === 0 ? "roles" : null;
  const open = picked ?? attention;
  const toggle = (key) => setPicked(open === key ? null : key);

  const becomePassenger = async () => {
    try {
      await apiPost("/api/memberships", { cityId, role: "passenger" });
      setNotice("Вы записаны пассажиром. Статус зависит от города.");
      await refreshMe();
    } catch (err) {
      setError(err.message);
    }
  };

  // One role per city: an application replaces the current role
  const currentRole = memberships.find(
    (membership) => membership.status === "ACTIVE" || membership.status === "PENDING"
  );
  const currentRoleLabel = currentRole ? ROLE_LABELS[currentRole.role] : null;
  // Dispatcher and region admin are the city staff: an application would wipe
  // their role before review, so the form is not offered to them
  const staffRole =
    currentRole?.role === "dispatcher" || currentRole?.role === "region_admin";

  const confirmSwitch = async () => {
    if (!currentRole || currentRole.role === "driver") return true;
    return confirm({
      title: "Сменить роль?",
      text: `Сейчас вы ${currentRoleLabel ?? "другая роль"} в этом городе. Заявка заменит её.`,
      okText: "Сменить роль",
      danger: true,
    });
  };

  const submitDriver = async (event) => {
    event.preventDefault();
    setError(null);
    if (!(await confirmSwitch())) return;
    try {
      await apiPost("/api/memberships", {
        cityId,
        role: "driver",
        displayName: driverForm.displayName,
        phone: withRussianPrefix(driverForm.phone),
        car: {
          color: driverForm.color,
          plate: driverForm.plate,
          ...(driverForm.brand && { brand: driverForm.brand }),
        },
      });
      setNotice("Анкета отправлена. Диспетчер проверит её и даст ответ.");
      await refreshMe();
    } catch (err) {
      setError(err.message);
    }
  };

  const submitDispatcher = async (event) => {
    event.preventDefault();
    setError(null);
    if (!(await confirmSwitch())) return;
    try {
      await apiPost("/api/applications", {
        cityId,
        type: "dispatcher",
        ...(dispatcherNote && { note: dispatcherNote }),
      });
      setNotice("Заявка отправлена. Её рассмотрит админ региона.");
      setDispatcherNote("");
    } catch (err) {
      setError(err.message);
    }
  };

  const rolesHint =
    memberships.length === 0
      ? "вы пока не в такси этого города"
      : memberships
          .map((m) => `${ROLE_LABELS[m.role] ?? m.role} · ${STATUS_LABELS[m.status] ?? m.status}`)
          .join(", ");

  // Заявка только на те роли, которых в городе ещё нет
  const applyRoles = [
    ...(!driverMembership ? [["driver", "водителем"]] : []),
    ...(!hasDispatcher ? [["dispatcher", "диспетчером"]] : []),
  ];
  const applyTitle =
    applyRoles.length === 1
      ? `Стать ${applyRoles[0][1]}`
      : "Стать водителем или диспетчером";

  return (
    <div className="p-4 space-y-3 overflow-y-auto h-full max-w-lg mx-auto w-full">
      {(error || notice) && (
        <div className={`taxi-banner ${error ? "is-error" : "is-notice"}`} role="status">
          <span className="min-w-0 break-words">{error ?? notice}</span>
          <button
            type="button"
            aria-label="Скрыть сообщение"
            onClick={() => {
              setError(null);
              setNotice(null);
            }}
          >
            <TaxiIcon name="close" size={16} />
          </button>
        </div>
      )}

      {/* Кто вы здесь: имя, логин, роль и город — без раскрытия разделов */}
      <div className="glass rounded-2xl taxi-summary" data-tour="cab-summary">
        <span className="taxi-summary-avatar"><TaxiIcon name="profile" size={24} /></span>
        <div className="min-w-0 flex-1">
          <div className="taxi-summary-name">{me?.user?.name ?? me?.user?.username ?? "Профиль"}</div>
          <div className="taxi-summary-sub">@{me?.user?.username ?? "—"}</div>
          <div className="taxi-chips">
            <Chip tone={currentRole ? "accent" : "danger"}>
              {currentRoleLabel ?? "Роль не выбрана"}
            </Chip>
          </div>
        </div>
      </div>

      <AccountCard
        me={me}
        refreshMe={refreshMe}
        onError={setError}
        onNotice={setNotice}
        open={open === "profile"}
        onToggle={() => toggle("profile")}
      />

      <Disclosure
        id="cab-roles"
        icon="passenger"
        title="Мои роли"
        hint={rolesHint}
        open={open === "roles"}
        onToggle={() => toggle("roles")}
      >
        {memberships.length === 0 && (
          <div className="text-[13px] text-secondary-text">
            В выбранном городе у вас пока нет роли. Заказывать такси можно после
            записи пассажиром.
          </div>
        )}
        {memberships.map((membership) => (
          <div key={membership._id} className="taxi-kv">
            <span className="taxi-kv-label flex items-center gap-1.5">
              <TaxiIcon name={ROLE_ICONS[membership.role] ?? "unknown"} size={16} />
              {ROLE_LABELS[membership.role] ?? membership.role}
            </span>
            <span className="taxi-kv-value">
              <Chip tone={STATUS_TONES[membership.status]}>
                {STATUS_LABELS[membership.status] ?? membership.status}
              </Chip>
              {membership.status === "REJECTED" && membership.reason && (
                <span className="block text-[11.5px] text-danger">{membership.reason}</span>
              )}
            </span>
          </div>
        ))}
        {!hasPassenger && (
          <div className="taxi-acc-actions">
            <button
              className="taxi-btn is-accent"
              onClick={async () => {
                if (!(await confirmSwitch())) return;
                becomePassenger();
              }}
            >
              <TaxiIcon name="passenger" size={17} /> Заказывать такси
            </button>
          </div>
        )}
        <LeaveRoleButtons
          memberships={memberships}
          refreshMe={refreshMe}
          onError={setError}
          onNotice={setNotice}
          onConfirm={confirm}
        />
      </Disclosure>

      {driverProfile && (
        <DriverCard
          profile={driverProfile}
          cityId={cityId}
          refreshMe={refreshMe}
          onError={setError}
          onNotice={setNotice}
          open={open === "driver"}
          onToggle={() => toggle("driver")}
        />
      )}

      {!staffRole && (!driverMembership || !hasDispatcher) && (
        <Disclosure
          id="cab-apply"
          icon="plus"
          title={applyTitle}
          hint={
            currentRoleLabel
              ? `заменит роль «${currentRoleLabel}»`
              : "анкета на проверку диспетчеру"
          }
          open={open === "apply"}
          onToggle={() => toggle("apply")}
        >
          {!driverMembership && (
            <form className="flex flex-col gap-2" onSubmit={submitDriver}>
              {applyRoles.length > 1 && <div className="taxi-acc-title">Водителем</div>}
              <input
                className="taxi-field"
                placeholder="Имя для пассажиров"
                aria-label="Имя для пассажиров"
                required
                value={driverForm.displayName}
                onChange={(event) => setDriverForm({ ...driverForm, displayName: event.target.value })}
              />
              <input
                className="taxi-field"
                placeholder="Телефон +7XXXXXXXXXX"
                aria-label="Телефон"
                inputMode="tel"
                required
                value={driverForm.phone}
                onChange={(event) => setDriverForm({ ...driverForm, phone: event.target.value })}
              />
              <div className="flex gap-2">
                <input
                  className="taxi-field flex-1"
                  placeholder="Цвет"
                  aria-label="Цвет машины"
                  required
                  value={driverForm.color}
                  onChange={(event) => setDriverForm({ ...driverForm, color: event.target.value })}
                />
                <input
                  className="taxi-field w-28"
                  placeholder="Номер"
                  aria-label="Госномер"
                  required
                  value={driverForm.plate}
                  onChange={(event) => setDriverForm({ ...driverForm, plate: event.target.value })}
                />
                <input
                  className="taxi-field w-28"
                  placeholder="Марка"
                  aria-label="Марка машины"
                  value={driverForm.brand}
                  onChange={(event) => setDriverForm({ ...driverForm, brand: event.target.value })}
                />
              </div>
              <button className="taxi-btn is-accent" type="submit">
                <TaxiIcon name="send" size={17} /> Отправить анкету
              </button>
            </form>
          )}

          {!hasDispatcher && (
            <form className="flex flex-col gap-2" onSubmit={submitDispatcher}>
              {applyRoles.length > 1 && <div className="taxi-acc-title">Диспетчером</div>}
              <textarea
                className="taxi-field"
                rows={2}
                placeholder="Коротко о себе (необязательно)"
                aria-label="О себе для заявки диспетчером"
                value={dispatcherNote}
                onChange={(event) => setDispatcherNote(event.target.value)}
              />
              <button className="taxi-btn is-accent" type="submit">
                <TaxiIcon name="send" size={17} /> Подать заявку
              </button>
            </form>
          )}
        </Disclosure>
      )}

      <Disclosure
        id="cab-city"
        icon="map"
        title="Город"
        hint={`${city?.name ?? "не выбран"} · ${cityStatusShort(city?.status)}`}
        open={open === "city"}
        onToggle={() => toggle("city")}
      >
        <Kv label="Название" value={city?.name} />
        <Kv
          label="Статус"
          value={<Chip tone={city?.status === "ACTIVE" || city?.status === "PILOT" ? "success" : "warn"}>{cityStatusLabel(city?.status)}</Chip>}
        />
        <Kv label="Часовой пояс" value={city?.tz} />
        <div className="text-[12px] text-secondary-text">
          Заказать такси можно в городах со статусом «Пилот» или «Полностью
          активен».
        </div>
      </Disclosure>

      {onStartTour && trainableRoles.length > 0 && (
        <Disclosure
          id="cab-training"
          icon="route"
          title="Обучение"
          hint="короткий тур с учебным заказом"
          open={open === "training"}
          onToggle={() => toggle("training")}
          tour="cab-training"
        >
          <div className="text-[13px] text-secondary-text">
            Учебный заказ видят только вы — настоящие водители и пассажиры его не
            получают. Тур можно пройти заново в любой момент.
          </div>
          <div className="taxi-acc-actions" data-tour="cab-training-replay">
            {trainableRoles.map(([tourRole, label]) => (
              <button
                key={tourRole}
                type="button"
                className="taxi-btn is-accent"
                onClick={() => onStartTour(tourRole)}
              >
                <TaxiIcon name={tourRole} size={17} /> Обучение {label}
                {trained(tourRole) ? " · пройдено" : ""}
              </button>
            ))}
          </div>
        </Disclosure>
      )}

      {onLogout && (
        <button type="button" className="taxi-logout press" onClick={onLogout}>
          <TaxiIcon name="exit" size={20} /> Выйти из аккаунта
        </button>
      )}
    </div>
  );
};

export default RolesSection;
